<template>
  <div>
    <nav-bar />
    <v-container class="mt-10 pt-5">
      <!-- Page Header ########################################### -->
      <header-page
        titleText="Gestão de Instituições"
        titleIcon="fal fa-hands-heart"
      />

      <!-- Page Body ########################################### -->
      <v-container pa-6>
        <v-row justify="space-between">
          <v-col cols="auto">
            <p class="list-labels">Lista de Instituições</p>
          </v-col>
          <v-skeleton-loader
            ref="skeleton"
            :boilerplate="false"
            type="table-tfoot"
            :tile="false"
            class="ml-auto"
            v-show="loading"
            min-width="300px"
          ></v-skeleton-loader>
          <v-col cols="auto" v-show="!loading">
            <v-container grid-list-md pa-0>
              <v-layout justify-start wrap justify-sm-end>
                <v-flex col-12 col-sm-12>
                  <v-combobox
                    v-model="currentSearchTerm"
                    :loading="loading"
                    :items="items"
                    label="Buscar"
                    outlined
                    clearable
                    dense
                    @change="addSearchTerm($event, true)"        
                  ></v-combobox>
                </v-flex>
                <v-flex flex-grow-0 col-12 col-sm-4 v-show="false">
                  <v-select
                    v-model="selectedOrderBy"
                    :items="orderByOptions"
                    label="Ordenar por"
                    item-text="name"
                    item-value="id"
                    outlined
                    hide-details="auto"
                    dense
                    solo
                    flat
                    @change="loadInstitutions"
                  ></v-select>
                </v-flex>
              </v-layout>
            </v-container>
          </v-col>
        </v-row>
      </v-container>

      <!-- Search Results ########################################### -->
      <search-result
        inset
        :totalResults="totalNumberOfInstitutions"
        v-model="searchTerms"
        @itemRemoved="searchTermRemoved()"
        :addButtonDestiny="routes.app.InstitutionRegisterNew"
        v-if="isAdminOrBenefactor"
      />

      <v-skeleton-loader
        ref="skeleton"
        :boilerplate="false"
        type="table-tbody"
        :tile="false"
        class="mx-auto"
        v-show="loading"
      ></v-skeleton-loader>

      <!-- Page Body ########################################### -->
      <v-container>
        <div>
          <template v-for="(institution, index) in institutions">
            <v-divider
              v-if="index > 0"
              :key="'Institution' + index"
            ></v-divider>
            <v-layout :key="institution.id" class="main-list-item" align-center>
              <!-- dados -->
              <v-col cols="12" md="6">
                <v-flex pl-2 pt-sm-0 pt-12>
                  <span>{{ institution.entityViewModel.socialReason }}</span>
                  <h4 class="font-weight-regular">
                    {{ institution.entityViewModel.name }}
                    <span class="caption op-5">
                      ({{
                        institution.entityViewModel.document | FormatDocument
                      }})</span
                    >
                  </h4>
                </v-flex>
              </v-col>

              <!-- tools #########################-->
              <div class="tools">
                <v-container fluid pa-0>
                  <v-row no-wrap align="center" no-gutters>
                    <!-- creation date  #########################-->
                    <v-col cols="auto">
                      <v-layout align-end column>
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-flex>
                              <v-icon
                                color="var(--text)"
                                class="mr-2"
                                size="16"
                                v-on="on"
                                >fal fa-user-tag</v-icon
                              >
                              <span class="tiny-label">{{
                                GetInstitutionAge(institution.entityViewModel)
                              }}</span>
                            </v-flex>
                          </template>
                          <span
                            >Cadastrado em:
                            {{
                              institution.entityViewModel.creationDate
                                | FormatDate
                            }}</span
                          >
                        </v-tooltip>
                      </v-layout>
                    </v-col>

                    <!-- context menu - mobile #########################-->
                    <v-menu bottom left>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          icon
                          v-on="on"
                          color="var(--gray)"
                          class="ml-3 d-flex d-md-none"
                        >
                          <v-icon>far fa-ellipsis-v</v-icon>
                        </v-btn>
                      </template>

                      <v-list dense>
                        <v-list-item v-for="(item, i) in contextMenu" :key="i">
                        </v-list-item>
                      </v-list>
                    </v-menu>

                    <!-- edit & on/off #########################-->
                    <v-col cols="auto">
                      <v-layout
                        align-center
                        class="d-none d-md-flex flex-grow-0"
                      >
                        <v-flex pl-6>
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                v-if="isAdminOrBenefactor"
                                icon
                                fab
                                small
                                color="var(--gray)"
                                class="hover-red"
                                v-on="on"
                                @click="editInstitution(institution)"
                              >
                                <v-icon size="20">fal fa-pen</v-icon>
                              </v-btn>
                            </template>
                            <span>Editar</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex pl-6>
                          <v-switch
                            v-if="isAdminOrBenefactor"
                            v-model="institution.entityViewModel.active"
                            @change="
                              updateInstitutionStatus(
                                institution.entityViewModel.id
                              )
                            "
                            inset
                          >
                          </v-switch>
                        </v-flex>
                      </v-layout>
                    </v-col>
                  </v-row>
                </v-container>
              </div>
            </v-layout>
          </template>
        </div>
      </v-container>
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import NavBar from "@/components/NavBar";
import HeaderPage from "@/components/HeaderPage";
import SearchResult from "@/components/SearchResult";
import EntityService from "@/scripts/services/entity.service";
import Routes from "@/router/names.js";
import InstitutionOrderByOptions from "@/scripts/enums/institutionOrderByOptions.enum";
import CommonHelper from "@/scripts/helpers/common.helper";

export default {
  name: "InstitutionManagement",
  components: {
    NavBar,
    HeaderPage,
    SearchResult
  },
  data: () => ({
    routes: Routes,
    entityService: new EntityService(),
    loadingCount: false,
    loadingList: false,
    institutions: [],
    totalNumberOfInstitutions: 0,
    selectedInstitution: null,
    page: 1,
    items: [],
    dialog: false,
    searchTerms: [],
    searchWaitTime: 2000,
    selectedOrderBy: InstitutionOrderByOptions.Name,
    currentSearchTerm: "",
    orderByOptions: [
      { id: InstitutionOrderByOptions.CreationDate, name: "Ordenar por data" },
      { id: InstitutionOrderByOptions.Name, name: "Ordenar por nome" },
      { id: InstitutionOrderByOptions.Status, name: "Ordenar por status" }
    ],
    contextMenu: [
      { icon: "fal fa-pen", menu: "Editar" },
      { icon: "fal fa-ban", menu: "Desativar" }
    ]
  }),
  mounted() {
    this.loadInstitutions();
  },
  computed: {
    ...mapGetters("userModule", ["loggedEntityId", "isAdminOrBenefactor"]),
    loading() {
      return this.loadingList || this.loadingCount;
    }
  },
  methods: {
    ...mapActions(["setSelectedInstitution"]),
    getFilters() {
      return {
        orderBy: this.selectedOrderBy,
        currentPage: this.currentPage - 1,
        pageSize: this.selectedPagination,
        searchTerms: this.searchTerms,
        loggedEntityId: parseInt(this.loggedEntityId)
      };
    },
    loadInstitutions() {
      if (this.loading) return;
      this.loadingCount = true;
      this.loadingList = true;
      this.searchTimeout = null;
      this.entityService
        .countInstitutions(this.getFilters())
        .then(this.countInstitutionsCallBack)
        .finally(() => (this.loadingCount = false));
      this.entityService
        .listInstitutions(this.getFilters())
        .then(this.listInstitutionsCallBack)
        .finally(() => (this.loadingList = false));
    },
    countInstitutionsCallBack(data) {
      this.totalNumberOfInstitutions = data;
      this.loadingCount = false;
    },
    listInstitutionsCallBack(data) {
      this.institutions = data;
      if(this.items.length == 0) {
         this.institutions.forEach(element => {
          this.items.push(element.entityViewModel.name);
        });
        this.item = this.items.sort();
      }
      this.loadingList = false;
    },
    editInstitution(institution) {
      this.setSelectedInstitution(institution);
      this.$router.push({
        name: Routes.app.InstitutionRegister,
        params: { institutionId: institution.id }
      });
    },
    updateInstitutionStatus(entityId) {
      this.entityService
        .updateEntityStatus(entityId);
    },
    searchTermRemoved() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(
        this.loadInstitutions,
        this.searchWaitTime
      );
    },
    addSearchTerm(event, addAnyway) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(this.loadInstitutions, this.searchWaitTime);
      }
      if (event.key == "Enter" || addAnyway) {
        if (
          this.currentSearchTerm &&
          this.currentSearchTerm.length > 0 &&
          this.searchTerms.indexOf(this.currentSearchTerm) == -1
        )
          this.searchTerms.push(this.currentSearchTerm);
        this.currentSearchTerm = [];
        if (!this.searchTimeout)
          this.searchTimeout = setTimeout(this.loadInstitutions, this.searchWaitTime);
      }
    },
    GetInstitutionAge(entity) {
      return CommonHelper.GetEntityAge(entity);
    }
  }
};
</script>